import React from 'react';
import PropTypes from 'prop-types';
import { useRoutes, Navigate } from 'react-router-dom';
import ROUTE_PATH from '../config/routes.config';
import LoginView from '../views/auth/login.view';
import DashboardView from '../views/dashboard/dashboard.view';
import ContentChannelsView from '../views/content/channels.view';
import ContentVideosView from '../views/content/videos.view';
import AdminUsersView from '../views/admin-users/admin-users.view';
import ReportsOverviewView from '../views/reports/overview.view';
import PageNotFoundView from '../views/errors/not-found.view';
import LogoutView from '../views/auth/logout.view';
import ContentCategoriesView from '../views/content/categories.view';
import ContentSequenceView from '../views/content/sequence.view';
import {
  DEFAULT_CHANNEL,
  SHOW_ADMIN_USERS,
  SHOW_BULK_EMAIL,
  SHOW_CONTACTS,
  SHOW_CONTENT_CATEGORIES,
  SHOW_CONTENT_VIDEOS,
  SHOW_LANGUAGES,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REPORTS_ENGAGEMENT,
  SHOW_REPORTS_OVERVIEW_ADS,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_RESOURCES,
  SHOW_TABS,
  SUBCONTENT_CATEGORIES,
} from '../config/const.config';
import AdsReportsOverviewView from '../views/reports/ads-overview.view';
import EngagementReportsView from '../views/reports/engagement.view';
import ContentTabsView from '../views/content/tabs.view';
import ContactsView from '../views/contacts/contacts.view';
import ContentLanguagesView from '../views/content/languages.view';
import ContentResourcesView from '../views/content/resources.view';
import BulkEmailView from '../views/bulk-email/bulk-email.view';

const AppRouter = ({ isLoggedIn, categoriesTab, rerenderComponent }) => {
  const routeMappings = [];

  if (isLoggedIn) {
    routeMappings.push({
      path: ROUTE_PATH.LOGIN,
      element: <Navigate to={ROUTE_PATH.DASHBOARD} />,
      exact: true,
    });

    if (SHOW_OVERVIEW_DASHBOARD) {
      routeMappings.push({
        path: ROUTE_PATH.DASHBOARD,
        element: <DashboardView />,
        exact: true,
      });
    }

    if (!DEFAULT_CHANNEL) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_CHANNELS,
        element: <ContentChannelsView />,
        exact: true,
      });
    }

    if (SHOW_LANGUAGES) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_LANGUAGES,
        element: <ContentLanguagesView />,
        exact: true,
      });
    }

    if (SHOW_CONTENT_CATEGORIES) {
      if (SUBCONTENT_CATEGORIES) {
        for (let i = 0; i < categoriesTab.length; i += 1) {
          routeMappings.push({
            path:
              categoriesTab[i].status === 1
                ? `${ROUTE_PATH.CONTENT_CATEGORIES}/${categoriesTab[i].slug}`
                : null,
            element: (
              <ContentCategoriesView
                categoryTypeSlug={categoriesTab[i].slug}
                categoryTypeName={categoriesTab[i].name}
                categoryTypeId={categoriesTab[i].id}
              />
            ),
            exact: true,
          });
        }
      } else {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_CATEGORIES,
          element: <ContentCategoriesView />,
          exact: true,
        });
      }
    }

    if (SHOW_CONTENT_VIDEOS) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_VIDEOS,
        element: <ContentVideosView videoContentType="videos" />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.CONTENT_ADS,
        element: <ContentVideosView videoContentType="ads" />,
        exact: true,
      });
    }

    if (DEFAULT_CHANNEL) {
      for (let i = 0; i < categoriesTab.length; i += 1) {
        routeMappings.push({
          path:
            categoriesTab[i].status === 1
              ? `${ROUTE_PATH.CONTENT_CATEGORIES_SEQUENCE.replace(
                  ':slug',
                  categoriesTab[i].slug
                )}`
              : null,
          element: (
            <ContentSequenceView
              categoryType={categoriesTab[i].slug}
              categoryName={categoriesTab[i].name}
            />
          ),
          exact: true,
        });
      }
    }

    if (SHOW_TABS) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_TABS,
        element: <ContentTabsView rerenderComponent={rerenderComponent} />,
        exact: true,
      });
    }

    if (SHOW_CONTACTS) {
      routeMappings.push({
        path: ROUTE_PATH.CONTACTS,
        element: <ContactsView />,
        exact: true,
      });
    }

    if (SHOW_BULK_EMAIL) {
      routeMappings.push({
        path: ROUTE_PATH.BULK_EMAIL,
        element: <BulkEmailView />,
        exact: true,
      });
    }

    if (SHOW_RESOURCES) {
      routeMappings.push({
        path: ROUTE_PATH.RESOURCES,
        element: <ContentResourcesView />,
        exact: true,
      });
    }

    if (SHOW_ADMIN_USERS) {
      routeMappings.push({
        path: ROUTE_PATH.ADMIN_USERS,
        element: <AdminUsersView />,
        exact: true,
      });
    }

    if (SHOW_REPORTS_OVERVIEW_VIDEOS) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS,
        element: <ReportsOverviewView />,
        exact: true,
      });
    }

    if (SHOW_REPORTS_OVERVIEW_ADS) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_OVERVIEW_ADS,
        element: <AdsReportsOverviewView />,
        exact: true,
      });
    }

    if (SHOW_REPORTS_ENGAGEMENT) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_ENGAGEMENT,
        element: <EngagementReportsView />,
        exact: true,
      });
    }

    routeMappings.push({
      path: ROUTE_PATH.LOGOUT,
      element: <LogoutView />,
      exact: true,
    });
  }

  routeMappings.push({
    path: '*',
    element: isLoggedIn ? <PageNotFoundView /> : <LoginView />,
  });

  const routes = useRoutes(routeMappings);

  return routes;
};

AppRouter.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  categoriesTab: PropTypes.array.isRequired,
  rerenderComponent: PropTypes.func.isRequired,
};

export default AppRouter;
